import * as React from 'react'
import { Link } from 'react-router-dom'

import { isNil, isNumber } from 'lodash'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import { hoursToText as formatHours } from 'modules/core/lib/duration'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'

import { formatAmount, formatUnit } from 'modules/reports/lib/formatSupplement'
import fields from 'modules/reports/store/collections/fields'
import type Field from 'modules/reports/store/models/Field'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'

const getLinkLabel = (type: string) => {
  switch (type) {
    case 'contract_hiring_link':
      return i18n.t('custom_reports.link.contract_hiring_link')
    case 'document_download_url':
      return i18n.t('custom_reports.link.document_download_url')
    case 'document_link':
      return i18n.t('custom_reports.link.document_link')
    case 'employee_hiring_link':
      return i18n.t('custom_reports.link.employee_hiring_link')
    case 'employee_link':
      return i18n.t('custom_reports.link.employee_link')
    case 'expense_link':
      return i18n.t('custom_reports.link.expense_link')
    case 'leave_link':
      return i18n.t('custom_reports.link.leave_link')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const UNKNOWN = '–'

export const TYPES = {
  // Special types
  cohort: 'cohort',

  // Value types
  text: 'text',
  number: 'number',
  decimal: 'decimal',
  date: 'date',
  boolean: 'boolean',

  // UI types
  hours: 'hours',
  money: 'money',
  cents: 'cents',
  rating: 'rating',
  link: 'link',
  enum: 'enum',

  // Other/Removed?
  dropdown: 'dropdown',
  bytes: 'bytes',
  single_choice: 'single_choice',
  unit: 'unit',
  amount: 'amount',
  checkbox: 'checkbox',
} as const

export type FieldValueType = (typeof TYPES)[keyof typeof TYPES]

export const UI_GROUPS = {
  abscissa: 'x',
  ordinate: 'y',
}

export const NINE_BOX = {
  employee_name_path: 'custom_resources_competencies_ninebox.employee_name',
}

export const MAX_PROJECTIONS_BY_CHART_TYPE: Record<string, number> = {
  bars: 3,
  pie: 2,
  scatterplot: Infinity,
  lines: 3,
  area: 3,
  numeric_metric: 1,
  table: Infinity,
}

const AGGREGATED_FIELD_LENGTH = 3

export enum GROUPING_DATE_PARTS {
  day = 'day',
  month = 'month',
  year = 'year',
}

export enum AGGREGATION_FUNCTIONS {
  sum = 'sum',
  average = 'average',
  maximum = 'maximum',
  minimum = 'minimum',
  count = 'count',
  concat = 'concat',
}

export const translateAggregationFunction = (
  aggregationFunction: AGGREGATION_FUNCTIONS
): string => {
  switch (aggregationFunction) {
    case AGGREGATION_FUNCTIONS.sum:
      return i18n.t('custom_reports.aggregation_options.sum')
    case AGGREGATION_FUNCTIONS.average:
      return i18n.t('custom_reports.aggregation_options.average')
    case AGGREGATION_FUNCTIONS.maximum:
      return i18n.t('custom_reports.aggregation_options.maximum')
    case AGGREGATION_FUNCTIONS.minimum:
      return i18n.t('custom_reports.aggregation_options.minimum')
    case AGGREGATION_FUNCTIONS.count:
      return i18n.t('custom_reports.aggregation_options.count')
    case AGGREGATION_FUNCTIONS.concat:
      return i18n.t('custom_reports.aggregation_options.concat')
  }
}

export const ABILITIES = {
  aggregation: 'aggregation',
  grouping: 'grouping',
  ordering: 'ordering',
  filtering: 'filtering',
} as const

const COHORT_FORMATS: Record<string, string> = {
  day: 'L',
  month: 'MMM YYYY',
  year: 'YYYY',
}

const DEFAULT_COHORT = 'month'

type Formatter = (projection?: ProjectionParams, field?: Field) => (value: any) => any

const IDENTITY_FORMATTER = () => (value: any) => value
const I18N_NUMBER_FORMATTER = (projection?: ProjectionParams) => {
  const path = projection?.path

  const field = fields.find({
    path,
  })

  const isCents = field?.attributes?.ui_type == 'cents'

  return (value: any) => {
    const convertedValue = isCents ? value / 100 : value

    return isNil(value) || value === UNKNOWN ? UNKNOWN : i18n.formatNumber(convertedValue)
  }
}
const REMOVED_FIELD_FORMATTER = () => () => i18n.t('custom_reports.removed')

export const validateFieldType = (projection: ProjectionParams, value: unknown): boolean => {
  const { path } = projection

  const field = fields.find({
    path,
  })

  const type = field?.get('type')

  switch (type) {
    case TYPES.boolean:
      return typeof value === 'boolean'
    case TYPES.number:
      return typeof value === 'number'
    case TYPES.text:
      return typeof value === 'string'
    default:
      return true
  }
}

export const cleanHeaderNames = (name: string) => {
  const path = name.split('.')
  const [_dbFunction, table, field] = path

  return path.length === AGGREGATED_FIELD_LENGTH ? [table, field].join('.') : name
}

export const formatForSQL = (value: unknown, column: string) => {
  const fieldPath = cleanHeaderNames(column)

  const field = fields.find({
    path: fieldPath,
  })

  if (field) {
    const type = field.attributes.type
    const uiType = field.attributes.ui_type

    const typeFormatter = TYPE_FORMATTERS[uiType || type]

    // !typeFormatter.length skips functions which require a projection parameter
    if (typeFormatter && !typeFormatter.length) {
      return typeFormatter()(value)
    }
  }

  if (typeof value === 'boolean') {
    return TYPE_FORMATTERS.boolean?.()(value)
  }

  if (typeof value === 'number') {
    return TYPE_FORMATTERS.number?.()(value)
  }

  return value
}

const TYPE_FORMATTERS: Record<string, Formatter> = {
  number: I18N_NUMBER_FORMATTER,
  identifier: I18N_NUMBER_FORMATTER,
  boolean: () => (value: boolean | string | number | null) => {
    switch (value) {
      case '0':
      case 0:
      case 'FALSE':
      case 'false':
      case false:
        return i18n.t('custom_reports.boolean.false')
      case '1':
      case 1:
      case 'TRUE':
      case 'true':
      case true:
        return i18n.t('custom_reports.boolean.true')
      case null:
        return i18n.t('custom_reports.boolean.null')
      default:
        return value
    }
  },
  date:
    () =>
    (
      value: any // TODO: Reports does not distinguish between datetime and dates at the
    ) =>
      // moment so we need to use moment who will correctly process whatever we
      // throw at it. Obviously, the correct solution would be to implement support
      // for datetime data types in reports engine.
      isNil(value) || value === UNKNOWN ? UNKNOWN : moment(value).format('l'),
  cents: () => (value: any) => formatAmount(value),
  money: () => (value: number) => formatCurrency(value),
  cohort: (projection?: ProjectionParams) => (value: string) =>
    projection &&
    stringToDate(value, 'backend').format(COHORT_FORMATS[projection.group || DEFAULT_COHORT]),
  link: (projection?: ProjectionParams) => {
    const key = projection?.path.replace(/\./g, '_')

    return function renderLink(value: unknown) {
      if (typeof value !== 'string') return null

      const text = getLinkLabel(key ?? '')

      if (value.startsWith('http')) {
        return (
          <a href={value} target="_blank" rel="noreferrer">
            {text}
          </a>
        )
      }

      return (
        <Link to={value} target="_blank">
          {text}
        </Link>
      )
    }
  },
  amount: () => (value: any) => formatAmount(value),
  unit: () => (value: any) => formatUnit(value),
  hours: () => (value: string | number) => formatHours(value),
  enum: (_projection?: ProjectionParams, field?: Field) => {
    const options = field?.get('enum_options')

    return (value: any) => {
      const enumForValue = options?.find((option: any) => option.value === value)

      return enumForValue ? enumForValue.label : value
    }
  },
}

const GROUP_FORMATTERS: Record<string, Formatter> = {
  [GROUPING_DATE_PARTS.year]: IDENTITY_FORMATTER,
  [GROUPING_DATE_PARTS.month]: IDENTITY_FORMATTER,
}

const AGGREGATION_FORMATTERS: Record<string, Formatter> = {
  [AGGREGATION_FUNCTIONS.concat]: IDENTITY_FORMATTER,
  [AGGREGATION_FUNCTIONS.count]: I18N_NUMBER_FORMATTER,
  [AGGREGATION_FUNCTIONS.average]: I18N_NUMBER_FORMATTER,
  [AGGREGATION_FUNCTIONS.maximum]: I18N_NUMBER_FORMATTER,
  [AGGREGATION_FUNCTIONS.minimum]: I18N_NUMBER_FORMATTER,
  [AGGREGATION_FUNCTIONS.sum]: I18N_NUMBER_FORMATTER,
}

const formatUsingPercentage = (value: any) => (isNumber(value) ? (value / 100).toFixed(2) : value)

const FORMATTERS_FOR_GRAPHS: Record<string, Formatter> = {
  number: () => (value: any) => isNumber(value) ? value.toFixed(2) : value,
  amount: () => (value: any) => formatUsingPercentage(value),
  cents: () => (value: any) => formatUsingPercentage(value),
  money: () => (value: any) => formatUsingPercentage(value),
  cohort: (projection?: ProjectionParams) => (value: string) =>
    stringToDate(value, 'backend').format(COHORT_FORMATS[projection?.group || DEFAULT_COHORT]),
}

const UI_AGGREGATION_FORMATTERS = ['hours']

export const formatter = (projection: ProjectionParams) => {
  const { path, group, function: aggregation } = projection

  const field = fields.find({
    path,
  })

  if (!field) {
    return REMOVED_FIELD_FORMATTER()
  }

  const type = field.attributes.type
  const uiType = field.attributes.ui_type

  const formatterByType =
    (aggregation && UI_AGGREGATION_FORMATTERS.includes(uiType) && TYPE_FORMATTERS[uiType]) ||
    (aggregation && AGGREGATION_FORMATTERS[aggregation]) ||
    (group && GROUP_FORMATTERS[group]) ||
    TYPE_FORMATTERS[uiType] ||
    TYPE_FORMATTERS[type] ||
    IDENTITY_FORMATTER

  return formatterByType(projection, field)
}

export const formatterForGraph = (projection: ProjectionParams) => {
  const { path, function: aggregation } = projection

  let field = fields.find({
    path,
  })

  if (path === 'cohort' && !field) {
    // This is a temporal approach until:
    // - we have fully migrated all templates to cohorts
    // - any path points towards time_period.cohort field instead
    field = fields.find({
      path: 'time_period.cohort',
    })
  }

  if (!field) {
    return REMOVED_FIELD_FORMATTER()
  }

  const type = field.attributes.type
  const uiType = field.attributes.ui_type

  const formatterByType =
    (aggregation && AGGREGATION_FORMATTERS[aggregation]) ||
    FORMATTERS_FOR_GRAPHS[uiType] ||
    FORMATTERS_FOR_GRAPHS[type] ||
    TYPE_FORMATTERS[uiType] ||
    TYPE_FORMATTERS[type] ||
    IDENTITY_FORMATTER

  return formatterByType(projection, field)
}

export const argumentsByOperator = (operator: string): number => {
  if (operator === 'between') return 2

  if ([OPERATORS.null, OPERATORS.not_null].includes(operator)) return 0

  if (/(current|today|this_week|this_month)_/.test(operator)) return 0

  return 1
}

export const OPERATORS = {
  eq: 'eq',
  not_eq: 'not_eq',
  gteq: 'gteq',
  gt: 'gt',
  lteq: 'lteq',
  lt: 'lt',
  between: 'between',
  starts_with: 'starts_with',
  contains: 'contains',
  current_day: 'current_day',
  current_week: 'current_week',
  current_month: 'current_month',
  current_quarter: 'current_quarter',
  current_year: 'current_year',
  today_or_before: 'today_or_before',
  this_week_or_before: 'this_week_or_before',
  this_month_or_before: 'this_month_or_before',
  today_or_after: 'today_or_after',
  this_week_or_after: 'this_week_or_after',
  this_month_or_after: 'this_month_or_after',
  last_days: 'last_days',
  last_weeks: 'last_weeks',
  last_months: 'last_months',
  last_quarters: 'last_quarters',
  last_years: 'last_years',
  null: 'null',
  not_null: 'not_null',
}

const BOOLEAN_OPERATORS = [OPERATORS.eq, OPERATORS.not_eq]

const ENUM_OPERATORS = [
  OPERATORS.null,
  OPERATORS.not_null,
  OPERATORS.eq,
  OPERATORS.not_eq,
  OPERATORS.starts_with,
  OPERATORS.contains,
]

const NUMBER_OPERATORS = [
  ...ENUM_OPERATORS,
  OPERATORS.gteq,
  OPERATORS.gt,
  OPERATORS.lteq,
  OPERATORS.lt,
  OPERATORS.between,
]

const DATE_OPERATORS = [
  OPERATORS.gt,
  OPERATORS.gteq,
  OPERATORS.lt,
  OPERATORS.lteq,
  OPERATORS.between,
  OPERATORS.current_day,
  OPERATORS.current_week,
  OPERATORS.current_month,
  OPERATORS.current_quarter,
  OPERATORS.current_year,
  OPERATORS.today_or_before,
  OPERATORS.today_or_after,
  OPERATORS.this_week_or_before,
  OPERATORS.this_week_or_after,
  OPERATORS.this_month_or_before,
  OPERATORS.this_month_or_after,
  OPERATORS.last_days,
  OPERATORS.last_weeks,
  OPERATORS.last_months,
  OPERATORS.last_quarters,
  OPERATORS.last_years,
  OPERATORS.null,
  OPERATORS.not_null,
]

export const OPERATORS_BY_TYPE: { [index in FieldValueType]?: string[] } = {
  [TYPES.enum]: ENUM_OPERATORS,
  [TYPES.text]: ENUM_OPERATORS,
  [TYPES.dropdown]: ENUM_OPERATORS,
  [TYPES.boolean]: BOOLEAN_OPERATORS,
  [TYPES.single_choice]: ENUM_OPERATORS,
  [TYPES.number]: NUMBER_OPERATORS,
  [TYPES.bytes]: NUMBER_OPERATORS,
  [TYPES.cents]: NUMBER_OPERATORS,
  [TYPES.money]: NUMBER_OPERATORS,
  [TYPES.rating]: NUMBER_OPERATORS,
  [TYPES.date]: DATE_OPERATORS,
  [TYPES.unit]: ENUM_OPERATORS,
  [TYPES.amount]: NUMBER_OPERATORS,
  [TYPES.hours]: NUMBER_OPERATORS,
  [TYPES.checkbox]: BOOLEAN_OPERATORS,
}

const DATE_SEPARATORS = [
  OPERATORS.between,
  OPERATORS.current_year,
  OPERATORS.this_month_or_after,
  OPERATORS.last_years,
]

export const SEPARATORS_BY_TYPE: { [index in FieldValueType]?: string[] } = {
  [TYPES.date]: DATE_SEPARATORS,
}
